/**
 * Service Finder v2 — safety-first questionnaire that recommends the right
 * Functional LAB pathway, with strict eligibility rules and rich results.
 *
 * Key contract (see /app/memory/PRD.md iter 59):
 *   1. A red-flag safety screen runs FIRST. Any tick routes the user to
 *      "seek urgent care" and never to a package.
 *   2. All prices, durations and inclusions come from
 *      `@/config/servicePackages` — the same source Prices.jsx renders.
 *   3. Strict rules enforced by `recommend()`:
 *        - mode = "remote" → never recommend hands-on packages
 *        - format = "individual" → never recommend membership as PRIMARY
 *        - surgery + no clearance → gate on Initial Assessment, not a package
 *      Budget influences payment plan copy, NOT the pathway.
 *   4. Result carries: why-it-matches bullets, a summary of the answers,
 *      the exact inclusions, one alternative, deep-link "View full details"
 *      and a prefilled contact link.
 *
 * Localised via i18n `pub.finder.*` (see /lib/locales/en.js).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, Check, ChevronLeft, Phone,
         RotateCcw, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES } from "@/config/site";
import PackageDetailsModal from "@/components/PackageDetailsModal";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  FALLBACK_MEMBERSHIPS,
  ONE_OFF_SESSIONS,
  REHAB_PACKAGES,
  findEntry,
} from "@/config/servicePackages";
import { APPROVED_MEM_PRICE } from "@/config/approvedPricing2026";

// ---------------------------------------------------------------------------
// Question flow (skipped questions are handled in `nextStep` below)
// ---------------------------------------------------------------------------
const SAFETY_FLAGS = [
  "trauma", "weakness", "bladder", "systemic", "chest",
];

const STEPS = [
  { id: "safety", type: "multi", options: SAFETY_FLAGS.concat(["none"]) },
  { id: "goal",   type: "single", options: ["pain", "surgery", "return", "perform", "prevent", "unsure"] },
  { id: "surgeryDate",  type: "single", options: ["recent", "recovering", "past", "notyet"], showIf: (a) => a.goal === "surgery" },
  { id: "surgeryClear", type: "single", options: ["yes", "no", "unsure"], showIf: (a) => a.goal === "surgery" },
  { id: "area",     type: "single", options: ["lower", "upper", "spine", "whole", "na"] },
  { id: "duration", type: "single", options: ["acute", "subacute", "chronic", "na"], showIf: (a) => ["pain", "surgery", "unsure"].includes(a.goal) },
  { id: "activity", type: "single", options: ["sedentary", "reduced", "active", "elite"] },
  { id: "mode",     type: "single", options: ["clinic", "remote", "hybrid"] },
  { id: "format",   type: "single", options: ["individual", "package", "membership", "any"] },
];

// ---------------------------------------------------------------------------
// Recommendation engine — deterministic, comment-heavy for auditability.
// Returns { primary, alternative, why: [string], gate?: 'surgery_clearance' }.
// ---------------------------------------------------------------------------
function recommend(a) {
  const { goal, mode, format, activity, duration, surgeryClear } = a;
  const isRemote = mode === "remote";
  const wantsIndividual = format === "individual";
  const wantsMembership = format === "membership";
  const isElite = activity === "elite";
  const isChronic = duration === "chronic";
  const isAcute = duration === "acute";

  // Surgery — hard gate on clearance. Anyone without surgeon sign-off is
  // routed to an initial assessment, not a rehab package.
  if (goal === "surgery" && surgeryClear !== "yes") {
    return {
      primary: ONE_OFF_SESSIONS.initial,
      alternative: ONE_OFF_SESSIONS.teleRehab,
      gate: "surgery_clearance",
      why: [
        "You've had surgery but don't yet have clinical sign-off to start rehabilitation.",
        "A one-off assessment is the safest first step until your surgeon confirms it's safe to load.",
      ],
    };
  }

  // Fully-remote users → Digital Support Membership (no clinic visits).
  if (isRemote) {
    if (wantsIndividual) {
      return {
        primary: ONE_OFF_SESSIONS.teleRehab,
        alternative: findEntry("digital"),
        why: [
          "You want to work with us fully online.",
          "A one-off tele-rehab consultation is the fastest way to get expert eyes on your situation without committing to a plan.",
        ],
      };
    }
    return {
      primary: findEntry("digital"),
      alternative: ONE_OFF_SESSIONS.teleRehab,
      why: [
        "You want ongoing support delivered fully online — no clinic visits.",
        "Digital Support Membership gives you a personalised exercise programme, a monthly progress review and messaging support with your clinician.",
      ],
    };
  }

  // Individual-session preference — never recommend a membership as primary.
  if (wantsIndividual) {
    const primary = ONE_OFF_SESSIONS.initial;
    let alt = ONE_OFF_SESSIONS.followUp;
    if (goal === "return" || isElite) alt = findEntry("return-to-performance");
    else if (goal === "surgery") alt = findEntry("surgery-recovery");
    else if (goal === "pain") alt = findEntry("recover");
    return {
      primary,
      alternative: alt,
      why: [
        "You've told us you'd rather pay per session than commit to a plan.",
        "An initial assessment gives you a clinical opinion, a treatment session and a plan you can act on straight away — no ongoing commitment.",
      ],
    };
  }

  // Goal-driven pathways
  if (goal === "surgery") {
    return {
      primary: isElite ? findEntry("signature-programme") : findEntry("surgery-recovery"),
      alternative: findEntry("return-to-performance"),
      why: [
        "You're recovering from an operation and have clearance to start rehab.",
        "A structured 12–16 week programme matches the phase-by-phase healing timelines we see for most orthopaedic surgeries.",
      ],
    };
  }
  if (goal === "return") {
    return {
      primary: findEntry("return-to-performance"),
      alternative: isElite ? findEntry("signature-programme") : findEntry("recover"),
      why: [
        "You want to get back to sport, running or the gym with confidence.",
        "This 8–10 week pathway pairs objective return-to-sport testing with a periodised plan.",
      ],
    };
  }
  if (goal === "pain") {
    // Chronic + membership-preference → Progress Membership (monthly clinic
    // session + online review) is better than a one-shot package.
    if (isChronic && wantsMembership) {
      return {
        primary: findEntry("progress"),
        alternative: findEntry("recover"),
        why: [
          "You've had symptoms for more than three months and want ongoing support.",
          "Progress Membership pairs a monthly supervised session with an additional online review and weekly monitoring — enough consistency to make chronic issues shift.",
        ],
      };
    }
    return {
      primary: findEntry("recover"),
      alternative: isAcute ? ONE_OFF_SESSIONS.initial : findEntry("digital"),
      why: [
        "You want help reducing pain, restoring function and building a rehabilitation plan you can follow.",
        "The Recovery Programme pairs a full assessment with four hands-on sessions and online check-ins over roughly six weeks.",
      ],
    };
  }
  if (goal === "perform") {
    if (wantsMembership) {
      return {
        primary: findEntry("performance"),
        alternative: findEntry("return-to-performance"),
        why: [
          "You train regularly and want ongoing screening + performance support.",
          "Performance Membership is a monthly plan built for people who already train — one session, one review, remote programming.",
        ],
      };
    }
    return {
      primary: findEntry("signature-programme"),
      alternative: findEntry("performance"),
      why: [
        "You want to push your ceiling with clinical support around it.",
        "Signature Programme gives you unlimited access over 12 weeks — perfect for a focused block before a race or season.",
      ],
    };
  }
  if (goal === "prevent") {
    if (wantsMembership) {
      return {
        primary: findEntry("digital"),
        alternative: findEntry("progress"),
        why: [
          "You want to stay ahead of injury with light monthly maintenance.",
          "Digital Support Membership keeps you on a personalised programme with monthly clinician reviews — no face-to-face appointments needed.",
        ],
      };
    }
    return {
      primary: ONE_OFF_SESSIONS.initial,
      alternative: findEntry("digital"),
      why: [
        "You're not currently injured — an initial assessment sets a baseline you can measure yourself against.",
        "You can top up with individual sessions or step into a membership when it suits you.",
      ],
    };
  }
  // "Not sure" — always land safely on an initial assessment.
  return {
    primary: ONE_OFF_SESSIONS.initial,
    alternative: ONE_OFF_SESSIONS.teleRehab,
    why: [
      "You're not sure what you need yet — that's exactly what an initial assessment is for.",
      "A clinician will review your goals, screen your movement and recommend the exact pathway. No commitment beyond the session.",
    ],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ServiceFinder({ compact = false, onComplete }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [safetyPicks, setSafetyPicks] = useState([]);
  // Live package list — used to enrich the recommendation with admin-editable
  // copy (headline, short_description, card_highlights) and current pricing.
  const [apiPackages, setApiPackages] = useState([]);
  const [modalPkg, setModalPkg] = useState(null);

  useEffect(() => {
    api.get("/packages")
      .then((r) => setApiPackages((r.data || []).filter((p) => p.active !== false)))
      .catch(() => setApiPackages([]));
  }, []);

  // Merge the static recommend() entry with the live API row (if the API
  // has the same id). API wins for name/price/description/highlights so
  // admin edits are reflected here. Anything the API doesn't carry (like
  // one-off session sub-copy) falls through to the static config.
  const enrich = (entry) => {
    if (!entry) return null;
    const approvedMemPrice = APPROVED_MEM_PRICE[entry.id];
    const live = apiPackages.find((p) => p.id === entry.id);
    if (!live) {
      // No live row — still apply the approved membership display price.
      return approvedMemPrice != null
        ? { ...entry, price: approvedMemPrice, _checkoutDisabled: true }
        : entry;
    }
    // RUN A (preview): approved membership prices are display-only.
    const livePrice = typeof live.price === "number" ? live.price : entry.price;
    const displayPrice = approvedMemPrice != null ? approvedMemPrice : livePrice;
    return {
      ...entry,
      // Merge with a preference for admin-editable API fields
      name: live.name || entry.name,
      price: displayPrice,
      _checkoutDisabled: approvedMemPrice != null && approvedMemPrice !== livePrice,
      billing: entry.billing || (live.type === "membership" ? "month" : undefined),
      sub: live.short_description || live.tagline || entry.sub,
      feat: (live.card_highlights && live.card_highlights.length ? live.card_highlights : (entry.feat || live.features || [])),
      headline: live.headline,
      best_for: live.best_for,
      show_public_price: live.show_public_price !== false,
      badge: live.badge,
      popular: live.popular ?? entry.popular,
      // Preserve everything else the modal needs (full_description, terms…)
      __live: live,
    };
  };

  // Steps visible to THIS user (some are conditional).
  const visibleSteps = useMemo(
    () => STEPS.filter((s) => !s.showIf || s.showIf(answers)),
    [answers],
  );
  const total = visibleSteps.length;
  const current = visibleSteps[step];

  const progress = Math.round(
    ((step + (done || urgent ? 1 : 0)) / total) * 100,
  );

  const rec = useMemo(() => (done ? recommend(answers) : null), [done, answers]);
  // Re-derive enriched primary/alternative whenever API data lands.
  const primary = useMemo(() => enrich(rec?.primary), [rec, apiPackages]);
  const alternative = useMemo(() => enrich(rec?.alternative), [rec, apiPackages]);

  const setAnswer = (id, value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (step + 1 >= total) {
      setDone(true);
      onComplete?.(next);
    } else {
      setStep(step + 1);
    }
  };

  const submitSafety = () => {
    const flagged = safetyPicks.filter((p) => p !== "none");
    if (flagged.length > 0) {
      setUrgent(true);
      return;
    }
    setAnswers((a) => ({ ...a, safety: "cleared" }));
    setStep(1);
  };

  const back = () => {
    if (urgent) { setUrgent(false); return; }
    if (done)   { setDone(false); return; }
    if (step > 0) setStep(step - 1);
  };

  const reset = () => {
    setAnswers({}); setStep(0); setDone(false); setUrgent(false); setSafetyPicks([]);
  };

  // ---------- URGENT ----------
  if (urgent) {
    return (
      <div className={compact ? "" : "max-w-3xl mx-auto"} data-testid="sf-urgent">
        <div className="fl-card p-6 md:p-8 border-red-500/40" style={{ background: "linear-gradient(180deg, rgba(220,38,38,0.08), transparent 70%)" }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
              <AlertTriangle size={20} className="text-red-400" />
            </div>
            <div>
              <div className="fl-label text-red-400">{t("pub.finder.urgentEyebrow")}</div>
              <h3 className="fl-heading text-2xl md:text-3xl mt-1">{t("pub.finder.urgentTitle")}</h3>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--fl-muted)" }}>
            {t("pub.finder.urgentBody")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:111" className="fl-btn text-sm inline-flex items-center gap-2" data-testid="sf-urgent-nhs">
              <Phone size={14} /> {t("pub.finder.urgentCallNhs")}
            </a>
            <a href="https://www.nhs.uk/service-search/other-services/Accident-and-emergency-services/LocationSearch/428"
               target="_blank" rel="noopener noreferrer"
               className="fl-btn-ghost text-sm inline-flex items-center gap-2" data-testid="sf-urgent-ae">
              {t("pub.finder.urgentFindAe")} <ArrowRight size={14} />
            </a>
            <button type="button" onClick={back} className="text-xs underline text-zinc-500 hover:text-zinc-300"
                    data-testid="sf-urgent-back">
              {t("pub.finder.urgentBack")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- RESULT ----------
  if (done && rec) {
    const p = primary;
    const alt = alternative;
    const showPrice = p?.show_public_price !== false;
    const priceLabel = !showPrice
      ? "Contact us for more information"
      : p?.billing
      ? `£${Math.round(p.price)}/${p.billing}`
      : p?.full
      ? `£${p.full}`
      : p?.price
      ? `£${p.price}`
      : "—";
    const durLabel = p?.duration || (p?.billing ? t("pub.finder.rec.rollingMembership") : "");
    // Live API row (if available) — needed to open the shared PackageDetailsModal.
    const liveForModal = p?.__live || null;

    // Prefill for the contact form + WhatsApp
    const summaryText = summariseAnswers(answers, t);
    const contactHref = `/contact?topic=finder&pathway=${encodeURIComponent(p?.id || "")}&summary=${encodeURIComponent(summaryText)}`;
    const waMessage = `${DEFAULT_WA_MESSAGES.finder} My recommended service is: ${p?.name}. ${summaryText}`;
    const isMembership = Boolean(p?.billing);

    return (
      <div className={compact ? "" : "max-w-3xl mx-auto"} data-testid="sf-result">
        <div className="fl-card p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,69,0,0.18), transparent 70%)" }} />
          <div className="relative">
            <div className="fl-label inline-flex items-center gap-2" style={{ color: "var(--fl-orange)" }}>
              <Sparkles size={14} /> {t("pub.finder.resultEyebrow")}
            </div>
            <h3 className="fl-heading text-3xl md:text-4xl mt-3" data-testid="sf-result-title">{p?.name}</h3>
            {p?.headline && (
              <p className="fl-heading text-base md:text-lg italic mt-2" style={{ color: "var(--fl-muted)" }} data-testid="sf-result-headline">
                {p.headline}
              </p>
            )}
            <div className="flex items-baseline gap-3 mt-3 flex-wrap">
              <div className={`fl-heading text-[#f47a20] ${showPrice ? "text-2xl" : "text-base font-semibold"}`} data-testid="sf-result-price">{priceLabel}</div>
              {durLabel && <div className="text-xs uppercase tracking-widest text-zinc-500" data-testid="sf-result-duration">{durLabel}</div>}
            </div>
            {p?.sub && <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--fl-muted)" }} data-testid="sf-result-sub">{p.sub}</p>}
            {p?.best_for && (
              <p className="mt-3 text-xs leading-relaxed" style={{ color: "var(--fl-muted)" }} data-testid="sf-result-best-for">
                <b>Best for:</b> {p.best_for}
              </p>
            )}

            {rec.gate === "surgery_clearance" && (
              <div className="mt-5 rounded-md border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="sf-result-gate">
                <ShieldCheck className="text-amber-400 shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-amber-100/90 leading-relaxed">{t("pub.finder.gate.surgeryClearance")}</div>
              </div>
            )}

            {/* Why this matches you */}
            <div className="mt-6" data-testid="sf-result-why">
              <div className="fl-label text-[#f47a20] mb-2">{t("pub.finder.rec.why")}</div>
              <ul className="space-y-2 text-sm">
                {rec.why.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-zinc-300">
                    <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Inclusions */}
            {Array.isArray(p?.feat) && p.feat.length > 0 && (
              <div className="mt-6" data-testid="sf-result-inclusions">
                <div className="fl-label mb-2 text-zinc-400">{t("pub.finder.rec.inclusions")}</div>
                <ul className="grid md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-400">
                  {p.feat.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#f47a20] mt-1">·</span><span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isMembership && (
              <div className="mt-4 text-[11px] text-zinc-500" data-testid="sf-membership-notice">
                {t("pub.finder.rec.membershipNotice")}
              </div>
            )}

            {/* Answer summary */}
            <details className="mt-6 group" data-testid="sf-result-summary">
              <summary className="fl-label cursor-pointer text-zinc-400 group-open:text-zinc-200">
                {t("pub.finder.rec.summary")}
              </summary>
              <div className="mt-3 text-xs text-zinc-500 whitespace-pre-line">{summaryText}</div>
            </details>

            {/* CTAs — primary "Continue to booking" flows the visitor through
                sign-in → checkout / booking while preserving the recommended
                package/service. */}
            {(() => {
              const isMembership = !!p?.billing;
              const isContactOnly = p?.show_public_price === false;
              const isSession = !p?.billing && !isContactOnly && !!p?.id;
              // Resolve the final destination based on the type of thing recommended
              const targetPath = isMembership
                ? `/checkout/summary?package=${p.id}&plan=membership`
                : isContactOnly
                ? `/contact?topic=finder&package=${p.id}`
                : isSession
                ? `/app/appointments?service=${encodeURIComponent(p.name)}`
                : contactHref;
              // Anonymous visitors are bounced through /register with the
              // destination baked into the redirect param; once they've
              // signed in they land straight back on that flow.
              const continueHref = user || isContactOnly
                ? targetPath
                : `/register?redirect=${encodeURIComponent(targetPath)}`;
              const continueLabel = isContactOnly
                ? t("pub.finder.rec.book")
                : (user ? t("pub.finder.rec.book") : "Continue to booking");
              return (
                <div className="mt-7 flex flex-wrap gap-3">
                  {p?._checkoutDisabled ? (
                    <>
                      <span
                        className="text-sm inline-flex items-center gap-1 px-4 py-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 cursor-default"
                        title="Preview only — online joining reopens once checkout is updated to the new price."
                        data-testid="sf-cta-disabled"
                      >
                        Booking update in progress
                      </span>
                      <Link
                        to={`/contact?topic=membership&package=${encodeURIComponent(p.id || "")}`}
                        className="fl-btn-ghost text-sm"
                        data-testid="sf-cta-enquire"
                      >
                        Enquire about this plan <ArrowRight size={14} />
                      </Link>
                    </>
                  ) : (
                    <Link
                      to={continueHref}
                      className="fl-btn text-sm fl-cta-pulse"
                      data-testid="sf-cta-continue"
                    >
                      {continueLabel} <ArrowRight size={14} />
                    </Link>
                  )}
                  {liveForModal ? (
                    <button
                      type="button"
                      onClick={() => setModalPkg(liveForModal)}
                      className="fl-btn-ghost text-sm"
                      data-testid="sf-cta-details"
                    >
                      {t("pub.finder.rec.viewDetails")}
                    </button>
                  ) : (
                    <Link to={p?.anchor || "/prices"} className="fl-btn-ghost text-sm" data-testid="sf-cta-details">
                      {t("pub.finder.rec.viewDetails")}
                    </Link>
                  )}
                  <WhatsAppCTA message={waMessage} testid="sf-cta-wa" variant="soft">
                    {t("pub.wa.finder")}
                  </WhatsAppCTA>
                  <Link to="/contact" className="fl-btn-ghost text-sm" data-testid="sf-cta-contact">
                    Contact us other ways
                  </Link>
                  <button type="button" onClick={reset} className="text-xs underline text-zinc-500 hover:text-zinc-300 ml-auto"
                          data-testid="sf-restart">
                    <RotateCcw size={12} className="inline mr-1" /> {t("pub.finder.tryAgain")}
                  </button>
                </div>
              );
            })()}

            {/* Alternative */}
            {alt && alt.id !== p?.id && (
              <div className="mt-8 pt-6 border-t border-white/5" data-testid="sf-result-alt">
                <div className="fl-label text-zinc-400 mb-2">{t("pub.finder.rec.alt")}</div>
                <div
                  onClick={() => alt.__live ? setModalPkg(alt.__live) : null}
                  className={`block group ${alt.__live ? "cursor-pointer" : ""}`}
                  role={alt.__live ? "button" : undefined}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="fl-heading text-lg group-hover:text-[#f47a20] transition-colors">{alt.name}</div>
                    <div className="text-sm text-zinc-400 whitespace-nowrap">
                      {alt.show_public_price === false
                        ? "Contact us"
                        : alt.billing ? `£${Math.round(alt.price)}/${alt.billing}`
                          : alt.full ? `£${alt.full}` : `£${alt.price}`}
                    </div>
                  </div>
                  {alt.sub && <div className="text-xs text-zinc-500 mt-1 max-w-2xl">{alt.sub}</div>}
                  {alt.__live && (
                    <div className="text-[10px] text-[#f47a20] mt-2 uppercase tracking-widest" data-testid="sf-alt-details">
                      Tap to view full details →
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
              {t("pub.finder.disclaimer")}
            </div>
          </div>
        </div>
        {modalPkg && (
          <PackageDetailsModal pkg={modalPkg} onClose={() => setModalPkg(null)} isLoggedIn={!!user} />
        )}
      </div>
    );
  }

  // ---------- SAFETY (Q0) ----------
  if (current?.id === "safety") {
    const toggle = (id) => {
      // "none" is exclusive — picking it clears the others; picking anything
      // else clears "none".
      setSafetyPicks((picks) => {
        if (id === "none") return picks.includes("none") ? [] : ["none"];
        const without = picks.filter((p) => p !== "none");
        return without.includes(id) ? without.filter((p) => p !== id) : [...without, id];
      });
    };
    const hasPick = safetyPicks.length > 0;

    return (
      <div className={compact ? "" : "max-w-3xl mx-auto"} data-testid="service-finder">
        <Progress step={step} total={total} progress={progress} t={t} />
        <div className="fl-card p-6 md:p-8" data-testid="sf-step-safety">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#f47a20]/15 flex items-center justify-center shrink-0">
              <ShieldCheck size={18} className="text-[#f47a20]" />
            </div>
            <div>
              <div className="fl-label text-[#f47a20] mb-1">{t("pub.finder.safetyEyebrow")}</div>
              <h3 className="fl-heading text-2xl md:text-3xl leading-tight">
                {t("pub.finder.safetyTitle")}
              </h3>
            </div>
          </div>
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
            {t("pub.finder.safetySubtitle")}
          </p>

          <div className="mt-6 grid gap-2">
            {SAFETY_FLAGS.map((id) => (
              <SafetyCheckbox
                key={id}
                id={id}
                checked={safetyPicks.includes(id)}
                onToggle={toggle}
                label={t(`pub.finder.safety.${id}`)}
              />
            ))}
            <div className="mt-2 border-t border-white/5 pt-2">
              <SafetyCheckbox
                id="none"
                checked={safetyPicks.includes("none")}
                onToggle={toggle}
                label={t("pub.finder.safety.none")}
                emphasis="ok"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={back} disabled className="text-xs inline-flex items-center gap-1 opacity-40" data-testid="sf-back">
              <ChevronLeft size={14} /> {t("pub.finder.back")}
            </button>
            <button
              type="button"
              onClick={submitSafety}
              disabled={!hasPick}
              className="fl-btn text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="sf-safety-continue"
            >
              {t("pub.finder.safetyContinue")} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- STANDARD SINGLE-CHOICE ----------
  return (
    <div className={compact ? "" : "max-w-3xl mx-auto"} data-testid="service-finder">
      <Progress step={step} total={total} progress={progress} t={t} />
      <div className="fl-card p-6 md:p-8" data-testid={`sf-step-${current.id}`}>
        <h3 className="fl-heading text-2xl md:text-3xl leading-tight">
          {t(`pub.finder.q.${current.id}`)}
        </h3>
        {t(`pub.finder.qHint.${current.id}`, { defaultValue: "" }) && (
          <p className="mt-2 text-xs text-zinc-500">{t(`pub.finder.qHint.${current.id}`)}</p>
        )}
        <div className="mt-6 grid gap-2">
          {current.options.map((oid) => (
            <button
              key={oid}
              type="button"
              onClick={() => setAnswer(current.id, oid)}
              data-testid={`sf-opt-${current.id}-${oid}`}
              className="text-left px-4 py-3 rounded-md border border-white/10 bg-white/[0.02] text-zinc-100 hover:border-[#f47a20] hover:bg-white/[0.05] hover:-translate-y-px transition-[border-color,background-color,transform] duration-150"
            >
              {t(`pub.finder.o.${current.id}.${oid}`)}
            </button>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <button type="button" onClick={back} disabled={step === 0}
                  className="text-xs inline-flex items-center gap-1 disabled:opacity-40"
                  style={{ color: "var(--fl-muted)" }} data-testid="sf-back">
            <ChevronLeft size={14} /> {t("pub.finder.back")}
          </button>
          <button type="button" onClick={reset} className="text-xs inline-flex items-center gap-1"
                  style={{ color: "var(--fl-muted-2)" }} data-testid="sf-reset">
            <RotateCcw size={12} /> {t("pub.finder.startAgain")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Progress({ step, total, progress, t }) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="fl-label" style={{ color: "var(--fl-orange)" }}>{t("pub.finder.eyebrow")}</div>
        <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
          {t("pub.finder.stepOf", { n: step + 1, total })}
        </div>
      </div>
      <div className="h-1 rounded-full mb-6" style={{ background: "var(--fl-border)" }}>
        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: "var(--fl-orange)", transition: "width 200ms ease-out" }} />
      </div>
    </>
  );
}

function SafetyCheckbox({ id, checked, onToggle, label, emphasis }) {
  const isOk = emphasis === "ok";
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      data-testid={`sf-safety-${id}`}
      className={
        "text-left px-4 py-3 rounded-md border transition-all flex items-start gap-3 " +
        (checked
          ? isOk
            ? "border-emerald-500/60 bg-emerald-500/10"
            : "border-red-500/60 bg-red-500/10"
          : "border-white/10 bg-white/[0.02] hover:border-white/25")
      }
    >
      <span className={
        "mt-0.5 w-4 h-4 rounded-[3px] border flex items-center justify-center shrink-0 " +
        (checked
          ? isOk
            ? "border-emerald-400 bg-emerald-500/20"
            : "border-red-400 bg-red-500/20"
          : "border-white/25")
      }>
        {checked && <Check size={12} className={isOk ? "text-emerald-300" : "text-red-300"} />}
      </span>
      <span className="text-sm text-zinc-100 leading-snug">{label}</span>
    </button>
  );
}

/** Compose a human-readable one-line summary of the user's answers — used in
 * the contact prefill + WhatsApp CTA so the clinician sees the context. */
function summariseAnswers(a, t) {
  const parts = [];
  if (a.goal) parts.push(t(`pub.finder.o.goal.${a.goal}`));
  if (a.area && a.area !== "na") parts.push(t(`pub.finder.o.area.${a.area}`));
  if (a.duration && a.duration !== "na") parts.push(t(`pub.finder.o.duration.${a.duration}`));
  if (a.activity) parts.push(t(`pub.finder.o.activity.${a.activity}`));
  if (a.mode) parts.push(t(`pub.finder.o.mode.${a.mode}`));
  if (a.format) parts.push(t(`pub.finder.o.format.${a.format}`));
  if (a.surgeryDate) parts.push(t(`pub.finder.o.surgeryDate.${a.surgeryDate}`));
  if (a.surgeryClear) parts.push(t(`pub.finder.o.surgeryClear.${a.surgeryClear}`));
  return parts.filter(Boolean).join(" · ");
}

// Silence "unused" lint warnings for exports consumed elsewhere in tests.
export { recommend, summariseAnswers, FALLBACK_MEMBERSHIPS, REHAB_PACKAGES };
